import { EventEmitter, Injectable, Output } from '@angular/core';
import { AbcCore, CollectionHelper, CompareHelper, GUID } from '@barthauer/abc/core';
import {
  DataService as CoreDataService,
  ObjectKey,
  ObjectSpaceService,
  ObjectTypeInfoViewModel,
  PersistMode,
  PersistObjectModel,
  PersistObjectsModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  QueryOutputMode,
  ReadPicklistItemsModel,
  TargetColumnValue,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import { DataEntity } from '@barthauer/abc/graphics/adapters/open-layers';
import { ParameterHelper, ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { firstValueFrom, forkJoin, from, lastValueFrom, Observable } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { MaintenanceOrder, ResultWorkGeneration } from '../models/helpers';
import { MtBase } from '../models/MtBase';
import { MtObject } from '../models/MtObject';
import { MtTaskType } from '../models/MtTaskType';
import { MtObjectWork, MtWork } from '../models/MtWork';
import { MtWorkGenerationInfo } from '../models/MtWorkGenerationInfo';
import { MtWorkType } from '../models/MtWorkType';
import { MtResource } from '../models/resource.model';
import { CyclicWorkGenerationResult, PersistenceResult, WorkState } from '../models/WorkWizardOptions';
import { UserService } from '@barthauer/abc/core/authentication';
import { AppService } from '@barthauer/abc/maintenance/opera/core';

@Injectable({
  providedIn: 'root',
})
export class WorkService {
  @Output() generationFinished = new EventEmitter<ResultWorkGeneration>();
  @Output() generationFailed = new EventEmitter();
  @Output() raiseInfo = new EventEmitter();
  @Output() raiseErrorInfo = new EventEmitter();
  @Output() raiseWarningInfo = new EventEmitter();

  public static readonly MtWorkObjTypeName: string = 'MtWork';
  private static readonly MtTaskObjTypeName: string = 'MtTask';
  private static readonly AuftragObjTypeName: string = 'Auftrag';
  public static readonly MtObjectObjTypeName: string = 'MtObject';
  public static readonly MtResourceOrderName: string = 'MtResourceOrder';

  constructor(
    private abcCore: AbcCore,
    private objectSpaceService: ObjectSpaceService,
    private userService: UserService,
  ) {}

  public async generateWorks(
    workGenerationInfos: MtWorkGenerationInfo[],
    workState: WorkState,
    workTypeId: string,
    orderId: string,
    source: string,
    selectedIdResources?: string[],
  ): Promise<void> {
    try {
      const excludeObjects = await this.queryObjectsWithExistingWorks(workGenerationInfos, workTypeId).toPromise();
      const query = await this.createWorkPersistQuery(
        workGenerationInfos,
        workState,
        workTypeId,
        orderId,
        source,
        excludeObjects,
      ).toPromise();

      const results = await this.abcCore.data.executePersistObjectsQuery(query).toPromise();
      const generatedObjectKeys = this.retrieveObjectKeys(results);

      this.onMtWorksGenerated(generatedObjectKeys, excludeObjects, workTypeId);

      if (selectedIdResources && selectedIdResources.length > 0) {
        for (const resourceId of selectedIdResources) {
          await this.assignResourceToOrder([resourceId], orderId);
        }
      }
    } catch (err) {
      this.onGenerationFailed(err);
    }
  }

  public async generateWorksAtMaintenanceCyclesEvaluation(
    cycles: Map<string, MtWorkGenerationInfo[]>,
    workState: WorkState,
  ): Promise<CyclicWorkGenerationResult[]> {
    const tasks = Array.from(cycles.entries()).map(async ([workTypeId, workGenerationInfos]) => {
      try {
        await this.generateWorks(workGenerationInfos, workState, workTypeId, '', '0', []);
        return { WorkTypeId: workTypeId, Success: true } as CyclicWorkGenerationResult;
      } catch (err) {
        return { WorkTypeId: workTypeId, Success: false, Error: err } as CyclicWorkGenerationResult;
      }
    });

    const results = await Promise.all(tasks);
    return results;
  }

  public assignWorksToOrder(workIds: string[], orderId: string): void {
    const objectsToPersist: PersistObjectsModel = new PersistObjectsModel();
    const objQueries: TargetObjectData[] = [];

    for (const workId of workIds) {
      const workPersistQuery: TargetObjectData = new TargetObjectData();
      workPersistQuery.Mode = PersistMode.Update;
      workPersistQuery.ObjectKey = new ObjectKey();
      workPersistQuery.ObjectKey.Id = workId;
      workPersistQuery.ObjectKey.ObjectType = WorkService.MtWorkObjTypeName;
      const targetColumns: TargetColumnValue[] = [];
      targetColumns.push({
        Name: 'AuftragsId',
        Value: orderId,
      });
      workPersistQuery.TargetColumns = targetColumns;
      objQueries.push(workPersistQuery);
    }
    objectsToPersist.Objects = [objQueries];
    this.abcCore.data.executePersistObjectsQuery(objectsToPersist).subscribe();
  }

  public onGenerationFailed(err: any): void {
    this.generationFailed.emit(err);
  }

  public showInfo(infoMessage: string): void {
    this.raiseInfo.emit(infoMessage);
  }

  public showErrorInfo(errorMessage: string): void {
    this.raiseErrorInfo.emit(errorMessage);
  }

  public showWarningInfo(warningMessage: string): void {
    this.raiseWarningInfo.emit(warningMessage);
  }

  private onMtWorksGenerated(mtWorks: MtBase[], excludeObjects: MtBase[], workTypeId: string): void {
    const workType$ = this.queryWorkType(workTypeId);
    const mtWorks$ = this.queryWorks(mtWorks);

    workType$.subscribe(
      (workType) => {
        mtWorks$.subscribe(
          (mtWorksQueried) => {
            if (mtWorksQueried && workType) {
              const insertTaskQuery = WorkService.createTaskPersistQuery(mtWorksQueried, workType.taskTypes);
              this.abcCore.data.executePersistObjectsQuery(insertTaskQuery).subscribe(
                (results) => this.finished(mtWorksQueried, excludeObjects),
                (err) => this.onGenerationFailed(err),
              );
            } else {
              this.finished(mtWorksQueried, excludeObjects);
            }
          },
          (err) => this.onGenerationFailed(err),
        );
      },
      (err) => this.onGenerationFailed(err),
    );
  }

  private queryObjectsWithExistingWorks(
    workGenerationInfos: MtWorkGenerationInfo[],
    workTypeId: string,
  ): Observable<MtObjectWork[]> {
    const queryDocuments = this.createObjectsWithExistingWorksQueries(workGenerationInfos, workTypeId);
    const observables: Observable<MtObjectWork[]>[] = [];
    for (const queryDocument of queryDocuments) {
      observables.push(
        this.abcCore.data.executeReadObjectsQuery(queryDocument).pipe(
          map((results) => {
            const objects = this.getResultsArray<MtObjectWork>(results);
            return objects;
          }),
        ),
      );
    }

    return forkJoin(observables).pipe(
      map((objectsList) => {
        const objects: MtBase[] = [];
        for (const list of objectsList) {
          objects.push(...list);
        }
        return objects;
      }),
    );
  }

  public queryResources(): Promise<MtResource[]> {
    const queryDocument = this.CreateResourcesQuery();
    return this.abcCore.data
      .executeReadObjectsQuery(queryDocument)
      .pipe(
        map((results) => {
          let resources: MtResource[] = this.getResultsArray<MtResource>(results);
          return resources;
        }),
      )
      .toPromise();
  }

  public assignResourceToOrder(resourceId: string[], orderId: string): Promise<string | undefined> {
    const persistentQuery = this.createResourceOrderPersistentQuery(resourceId, orderId);

    return this.abcCore.data
      .executePersistObjectsQuery(persistentQuery)
      .pipe(
        map((results) => {
          const keys = this.retrieveObjectKeys(results);
          if (keys.length === 0) {
            return '';
          } else {
            return keys[0].Id;
          }
        }),
      )
      .toPromise();
  }

  public deleteResourceOrderAssignment(removeResourceId: string, resourceOrderId: string): Promise<boolean> {
    const persistentQuery = this.createResourceOrderRemoveAssignmentQuery(removeResourceId);
    return this.abcCore.data
      .executePersistObjectsQuery(persistentQuery)
      .pipe(
        map((results) => {
          const keys = this.retrieveObjectKeys(results);
          if (keys.length === 0) {
            return false;
          } else {
            return true;
          }
        }),
      )
      .toPromise();
  }

  private createResourceOrderRemoveAssignmentQuery(removeResourceId: string) {
    let queryModel: PersistObjectsModel = new PersistObjectsModel();
    let objQueries: TargetObjectData[] = [];
    let persistQuery: TargetObjectData = new TargetObjectData();

    persistQuery.Mode = PersistMode.Delete;
    persistQuery.ObjectKey = new ObjectKey();
    persistQuery.ObjectKey.ObjectType = WorkService.MtResourceOrderName;
    persistQuery.ObjectKey.Id = removeResourceId;

    objQueries.push(persistQuery);
    queryModel.Objects = [objQueries];
    return queryModel;
  }

  private createResourceOrderPersistentQuery(resourceId: string[], orderId: string): PersistObjectsModel {
    let queryModel: PersistObjectsModel = new PersistObjectsModel();
    let objQueries: TargetObjectData[] = [];

    for (const resId of resourceId) {
      const persistQuery: TargetObjectData = new TargetObjectData();
      persistQuery.Mode = PersistMode.Merge;
      persistQuery.ObjectKey = new ObjectKey();
      persistQuery.ObjectKey.ObjectType = WorkService.MtResourceOrderName;

      const targetColumns: TargetColumnValue[] = [];
      targetColumns.push(
        {
          Name: 'ParentId',
          Value: orderId,
        },
        {
          Name: 'IdRefResource',
          Value: resId,
        },
      );

      persistQuery.TargetColumns = targetColumns;
      objQueries.push(persistQuery);
    }

    queryModel.Objects = [objQueries];
    return queryModel;
  }

  private CreateResourcesQuery(): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = 'MtResource';
    query.OPath = 'ParentId = $CurrentMunicipalityId';

    const columns: QueryColumn[] = [];
    columns.push({ Name: 'Id', OPath: 'Id' });
    columns.push({ Name: 'ObjectType', OPath: 'ObjectType' });
    columns.push({ Name: 'Code', OPath: 'Code' });
    columns.push({ Name: 'Name', OPath: 'Name' });
    columns.push({ Name: 'Kind', OPath: 'Kind' });
    columns.push({ Name: 'Status', OPath: 'ref:Type' });
    columns.push({ Name: 'CostCenter', OPath: 'CostCenter' });
    columns.push({ Name: 'DefaultUnit', OPath: 'DefaultUnit' });
    columns.push({ Name: 'DefaultUnitPrice', OPath: 'DefaultUnitPrice' });
    columns.push({ Name: 'Login', OPath: 'Login' });
    columns.push({ Name: 'ParentResource', OPath: 'ref:IdRefResourceParent' });
    columns.push({ Name: 'Art', OPath: 'ref:IdRefResourceType' });

    query.Columns = columns;
    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];

    return queryDocument;
  }

  public queryResourcesOrder(auftragToOrderId: string): Promise<MtResource[]> {
    const queryDocument = this.SelectResourcesBasedOnOrderQuery(auftragToOrderId);
    return this.abcCore.data
      .executeReadObjectsQuery(queryDocument)
      .pipe(
        map((results) => {
          let resources: MtResource[] = this.getResultsArray<MtResource>(results);
          return resources;
        }),
      )
      .toPromise();
  }

  public SelectResourcesBasedOnOrderQuery(auftragToOrderIdExecute: string) {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = 'MtResourceOrder';
    query.OPath = `ParentId = '${auftragToOrderIdExecute}'`;

    const columns: QueryColumn[] = [];
    columns.push({ Name: 'Id', OPath: 'Id' });
    columns.push({ Name: 'ObjectType', OPath: 'ObjectType' });
    columns.push({ Name: 'IdRefResource', OPath: 'IdRefResource' });

    query.Columns = columns;
    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];

    return queryDocument;
  }

  public async queryWorksByOrderId(orderId: string): Promise<MtWork[]> {
    const queryDocument = this.createWorksQueryByOrderId(orderId);

    const works = await firstValueFrom(
      this.abcCore.data
        .executeReadObjectsQuery(queryDocument)
        .pipe(map((results) => this.getResultsArray<MtWork>(results))),
    );

    return await this.prepareWorkParentObjectTypeDesignation(works);
  }

  private async prepareWorkParentObjectTypeDesignation(works: MtWork[]): Promise<MtWork[]> {
    const mapping: Map<number, string> = new Map();

    for (let work of works) {
      const masterType: number | undefined = work.MasterObjectType;
      if (masterType !== undefined) {
        let masterTypeName: string | undefined = mapping.get(masterType);
        if (!masterTypeName) {
          const objTypeInfo = await firstValueFrom(this.objectSpaceService.getObjectType(masterType));
          if (!!objTypeInfo) {
            masterTypeName = objTypeInfo.displayText;
            mapping.set(masterType, masterTypeName);
          }
        }

        work.MasterObjectTypeDesignation = masterTypeName;
      }
    }
    return works;
  }

  public async updateWorkSortOrders(mtWorks: MtWork[]): Promise<PersistenceResult> {
    try {
      const updateQuery = await this.createUpdateWorkSortOrderQuery(mtWorks);
      const results = await firstValueFrom(
        this.abcCore.data.executePersistObjectsQuery(updateQuery).pipe(map((r) => this.retrieveObjectKeys(r))),
      );
      return { affectedCount: results.length, success: true };
    } catch (error) {
      return { affectedCount: 0, success: false, error };
    }
  }

  private async createUpdateWorkSortOrderQuery(mtWorks: MtWork[]): Promise<PersistObjectsModel> {
    let result: PersistObjectsModel = new PersistObjectsModel();

    let objQueries: TargetObjectData[] = [];
    for (let work of mtWorks) {
      let workPersistQuery: TargetObjectData = new TargetObjectData();

      workPersistQuery.Mode = PersistMode.Update;
      workPersistQuery.ObjectKey = new ObjectKey();
      workPersistQuery.ObjectKey.ObjectType = WorkService.MtWorkObjTypeName;
      workPersistQuery.ObjectKey.Id = work.Id;

      const targetColumns: TargetColumnValue[] = [];
      targetColumns.push({
        Name: 'SortOrder',
        Value: work.SortOrder ?? '',
      });

      workPersistQuery.TargetColumns = targetColumns;
      objQueries.push(workPersistQuery);
    }

    result.Objects = [objQueries];
    return result;
  }

  private createObjectsWithExistingWorksQueries(
    workGenerationInfos: MtWorkGenerationInfo[],
    workTypeId: string,
  ): QueryObjectsModel[] {
    const queryDocuments: QueryObjectsModel[] = [];

    for (const g of CollectionHelper.groupBy(workGenerationInfos, (_) => _.ScheduledWorkExecDate)) {
      let queryDocument: QueryObjectsModel | undefined = undefined;
      if (queryDocuments.length > 0) {
        queryDocument = queryDocuments[queryDocuments.length - 1];
      }
      if (!queryDocument || queryDocument.Parameters.length === 10) {
        queryDocument = new QueryObjectsModel();
        const query: Query = new Query();
        query.ObjectType = WorkService.MtObjectObjTypeName;
        queryDocument.ObjectSpaceName = 'BaSYS.Complete';
        queryDocument.OutputMode = QueryOutputMode.Json;
        queryDocument.ObjectQueries = [query];
        queryDocument.Parameters = [];

        const scheduledDate: Date = g.key!;
        const filter: string =
          "WorkTypeId='" +
          workTypeId +
          "' AND ScheduledExecutionDate = GetDate('" +
          WorkService.formatDate(scheduledDate) +
          "')";
        const columns: QueryColumn[] = [];
        columns.push({ Name: 'Id', OPath: 'Id' });
        columns.push({ Name: 'ObjectType', OPath: 'TypeName()' });
        columns.push({ Name: 'Designation', OPath: 'Designation' });

        columns.push({ Name: 'State', OPath: 'ref:Works[' + filter + '].State' });
        columns.push({
          Name: 'EarliestExecutionDate',
          OPath: 'Works[' + filter + '].EarliestExecutionDate',
        });
        columns.push({
          Name: 'ScheduledExecutionDate',
          OPath: 'Works[' + filter + '].ScheduledExecutionDate',
        });
        columns.push({
          Name: 'LatestExecutionDate',
          OPath: 'Works[' + filter + '].LatestExecutionDate',
        });
        columns.push({ Name: 'Order', OPath: 'Works[' + filter + '].Auftrag.Bezeichnung' });

        query.Columns = columns;
        queryDocuments.push(queryDocument);
      }
      let opath = queryDocument.ObjectQueries[0].OPath ?? '';
      if (opath) {
        opath += ' OR ';
      }
      const ids: string[] = [];
      for (const info of g) {
        ids.push(info.MtObjectId);
      }
      const idlist: string = ids.join(`','`);
      const parameterName = 'P' + queryDocument.Parameters.length;
      queryDocument.Parameters.push(new ParameterValue(parameterName, ParameterHelper.getDateValue(g.key)));
      opath += `Id IN ('${idlist}') AND Exists(Works[WorkTypeId='${workTypeId}' AND ScheduledExecutionDate = $${parameterName}])`;

      queryDocument.ObjectQueries[0].OPath = opath;
    }
    return queryDocuments;
  }

  private queryWorks(mtWorks: MtBase[]): Observable<MtWork[]> {
    const queryDocument = this.createWorksQuery(mtWorks);
    return this.abcCore.data.executeReadObjectsQuery(queryDocument).pipe(
      map((results) => {
        let works: MtWork[] = this.getResultsArray<MtWork>(results);
        return works;
      }),
    );
  }

  private getResultsArray<T>(results: ArrayBuffer): T[] {
    if (results && Array.isArray(results)) {
      return results;
    } else {
      return [];
    }
  }

  private getResultObject<T>(results: ArrayBuffer): T {
    const arr = this.getResultsArray<T>(results);
    if (arr?.length) {
      return arr[0];
    }
    return <T>undefined;
  }

  private createWorksQuery(mtWorks: MtBase[]): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = WorkService.MtWorkObjTypeName;
    const ids: string[] = [];
    if (mtWorks) {
      for (const w of mtWorks) {
        ids.push(w.Id ?? '');
      }
    }
    query.OPath = `Id In ('${ids.join(`','`)}')`;

    const columns: QueryColumn[] = [];

    columns.push({ Name: 'Id', OPath: 'Id' });
    columns.push({ Name: 'ObjectType', OPath: 'TypeName()' });
    columns.push({ Name: 'Designation', OPath: 'Designation' });
    columns.push({ Name: 'ObjectDesignation', OPath: 'Object.Designation' });
    columns.push({ Name: 'Status', OPath: 'ref:State' });
    columns.push({ Name: 'ExecutionFrom', OPath: 'EarliestExecutionDate' });
    columns.push({ Name: 'ExecutionPlanned', OPath: 'ScheduledExecutionDate' });
    columns.push({ Name: 'ExecutionTo', OPath: 'LatestExecutionDate' });
    columns.push({ Name: 'Order', OPath: 'Auftrag.Bezeichnung' });

    query.Columns = columns;

    const objectQuery = new Query();
    objectQuery.OPath = 'Object';
    objectQuery.Name = 'Objects';
    const objectColumns: QueryColumn[] = [];
    objectColumns.push({ Name: 'GeometryType', OPath: 'Typ' });
    objectColumns.push({ Name: 'GeObjectId', OPath: 'GeObjectId' });
    objectColumns.push({ Name: 'GeObjectType', OPath: 'GeObjectType' });
    objectQuery.Columns = objectColumns;
    query.ObjectQueries = [objectQuery];

    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];

    return queryDocument;
  }

  private createWorksQueryByOrderId(orderId: string): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = WorkService.MtWorkObjTypeName;
    const ids: string[] = [];

    query.OPath = `Auftrag.Id='${orderId}'`;

    const columns: QueryColumn[] = [];

    columns.push({ Name: 'Id', OPath: 'Id' });
    columns.push({ Name: 'ObjectType', OPath: 'TypeName()' });
    columns.push({ Name: 'Designation', OPath: 'Designation' });
    columns.push({ Name: 'ObjectDesignation', OPath: 'Object.Designation' });
    columns.push({ Name: 'Status', OPath: 'ref:State' });
    columns.push({ Name: 'ExecutionFrom', OPath: 'EarliestExecutionDate' });
    columns.push({ Name: 'ExecutionPlanned', OPath: 'ScheduledExecutionDate' });
    columns.push({ Name: 'ExecutionTo', OPath: 'LatestExecutionDate' });
    columns.push({ Name: 'Order', OPath: 'Auftrag.Bezeichnung' });
    columns.push({ Name: 'SortOrder', OPath: 'SortOrder', SortOrder: QueryColumnSortOrder.Ascending });
    columns.push({ Name: 'MasterObjectType', OPath: 'Object.GeObjectType' });
    columns.push({ Name: 'Street', OPath: 'ref:Object.IdRefStrasse' });
    columns.push({ Name: 'District', OPath: 'ref:Object.IdRefOrtsteil' });

    query.Columns = columns;

    const objectQuery = new Query();
    objectQuery.OPath = 'Object';
    objectQuery.Name = 'Objects';
    const objectColumns: QueryColumn[] = [];
    objectColumns.push({ Name: 'GeometryType', OPath: 'Typ' });
    objectColumns.push({ Name: 'GeObjectId', OPath: 'GeObjectId' });
    objectColumns.push({ Name: 'GeObjectType', OPath: 'GeObjectType' });
    objectQuery.Columns = objectColumns;
    query.ObjectQueries = [objectQuery];

    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];

    return queryDocument;
  }

  private finished(generatedWorks: MtWork[], excludedObjects: MtObjectWork[]): void {
    let result: ResultWorkGeneration = {
      generatedWorks: generatedWorks,
      excludedObjects: excludedObjects,
    };
    this.generationFinished.emit(result);
  }

  public static createTaskPersistQuery(
    mtWorks: MtWork[],
    mtTaskTypes: MtTaskType[],
    notifyTaskCreated?: (targetColumns: TargetColumnValue[], taskType: MtTaskType) => void,
  ): PersistObjectsModel {
    let ret: PersistObjectsModel = new PersistObjectsModel();

    let objQueries: TargetObjectData[] = [];
    for (let work of mtWorks) {
      for (let taskType of mtTaskTypes) {
        if (this.isRelevant(taskType, work.Objects)) {
          let taskPersistQuery: TargetObjectData = new TargetObjectData();

          taskPersistQuery.Mode = notifyTaskCreated ? PersistMode.Merge : PersistMode.Insert;
          taskPersistQuery.ObjectKey = new ObjectKey();
          taskPersistQuery.ObjectKey.ObjectType = this.MtTaskObjTypeName;

          const posDesig: string =
            taskType.positionTypes === undefined ? '' : (taskType.positionTypes[0].designation ?? '');
          const posTypeId: string = taskType.positionTypes === undefined ? '' : (taskType.positionTypes[0].Id ?? '');
          const allowMultiSelectResults: string =
            taskType.allowMultiSelectResults === null || taskType.allowMultiSelectResults === undefined
              ? ''
              : taskType.allowMultiSelectResults.toString();
          const allowSelectResultsFromSingleCategory: string =
            taskType.allowSelectResultsFromSingleCategory === null ||
            taskType.allowSelectResultsFromSingleCategory === undefined
              ? ''
              : taskType.allowSelectResultsFromSingleCategory.toString();
          const resultsRequired: string =
            taskType.resultsRequired === null || taskType.resultsRequired === undefined
              ? ''
              : taskType.resultsRequired.toString();
          const unitPrice: string = !taskType.unitPrice ? '' : taskType.unitPrice.toString();
          const description: string = !taskType.description ? '' : taskType.description?.toString();

          const relevantForEdges: string =
            taskType.relevantForEdges === null || taskType.relevantForEdges === undefined
              ? ''
              : taskType.relevantForEdges.toString();

          const relevantForEndNode: string =
            taskType.relevantForEndNode === null || taskType.relevantForEndNode === undefined
              ? ''
              : taskType.relevantForEndNode.toString();

          const relevantForMidNodes: string =
            taskType.relevantForMidNodes === null || taskType.relevantForMidNodes === undefined
              ? ''
              : taskType.relevantForMidNodes.toString();

          const relevantForStartNode: string =
            taskType.relevantForStartNode === null || taskType.relevantForStartNode === undefined
              ? ''
              : taskType.relevantForStartNode.toString();

          const targetColumns: TargetColumnValue[] = [];
          targetColumns.push(
            {
              Name: 'Designation',
              Value: new String(taskType.designation),
            },
            {
              Name: 'ParentId',
              Value: new String(work.Id),
            },
            {
              Name: 'SortOrder',
              Value: new String(taskType.sortOrder),
            },
            {
              Name: 'PosDesignation',
              Value: new String(posDesig),
            },
            {
              Name: 'TaskTypeId',
              Value: new String(taskType.Id),
            },
            {
              Name: 'PositionTypeId',
              Value: new String(posTypeId),
            },

            {
              Name: 'AllowMultiSelelectResults', //this is a typo in data structure for present
              Value: new String(allowMultiSelectResults),
            },
            {
              Name: 'AllowSelectResultsFromSingleCategory',
              Value: new String(allowSelectResultsFromSingleCategory),
            },
            {
              Name: 'ResultsRequired',
              Value: new String(resultsRequired),
            },
            {
              Name: 'UnitPrice',
              Value: new String(unitPrice),
            },
            {
              Name: 'Description',
              Value: new String(description),
            },
            {
              Name: 'RelevantForEdges',
              Value: new String(relevantForEdges),
            },
            {
              Name: 'RelevantForEndNode',
              Value: new String(relevantForEndNode),
            },
            {
              Name: 'RelevantForMidNodes',
              Value: new String(relevantForMidNodes),
            },
            {
              Name: 'RelevantForStartNode',
              Value: new String(relevantForStartNode),
            },
          );
          if (notifyTaskCreated) {
            const taskId = GUID.newGUID();
            targetColumns.push({
              Name: 'Id',
              Value: taskId,
            });
            notifyTaskCreated(targetColumns, taskType);
          }

          taskPersistQuery.TargetColumns = targetColumns;
          objQueries.push(taskPersistQuery);
        }
      }
    }

    ret.Objects = [objQueries];
    return ret;
  }

  private static isRelevant(taskType: MtTaskType, mtObjects: MtObject[]): boolean {
    if (!mtObjects || mtObjects.length == 0) {
      return false;
    }

    let mtObject = mtObjects[0];
    const relevantForEdges = taskType.relevantForEdges;

    const relevantForEndNode = taskType.relevantForEndNode;
    const relevantForStartNode = taskType.relevantForStartNode;
    const relevantForMidNodes = taskType.relevantForMidNodes;
    const relevantForNodes = relevantForEndNode || relevantForStartNode || relevantForMidNodes;

    let isRelevant: boolean = true;

    if (!relevantForEdges && !relevantForNodes)
      //all unchecked, standard situation, taking to be as relevant
      isRelevant = true;
    else if (mtObject.GeometryType === null || mtObject.GeometryType === undefined) isRelevant = true;
    else if (mtObject.GeometryType == '0') isRelevant = relevantForNodes === true;
    else if (mtObject.GeometryType == '1') isRelevant = relevantForEdges === true;
    else isRelevant = true;

    return isRelevant;
  }

  public retrieveObjectKeys(results: ArrayBuffer): MtBase[] {
    let ret: MtBase[] = [];
    if (results) {
      return this.getResultObject<MtBase[]>(results) ?? [];
    }

    return ret;
  }

  public createAuftragPersistQuery(auftrag: MaintenanceOrder): PersistObjectModel {
    //auftragName: string, typ: number = 2
    const auftragsName = auftrag.designation ?? '';
    const state = auftrag.state ?? 0;
    const auftragDate = auftrag.date ?? new Date();
    const auftragsNumber = auftrag.number ?? '';
    const auftragsRemark = auftrag.remark ?? '';

    const ret: PersistObjectModel = new PersistObjectModel();
    let query: TargetObjectData = new TargetObjectData();

    query.Mode = PersistMode.Insert;
    query.ObjectKey = new ObjectKey();

    query.ObjectKey.ObjectType = WorkService.AuftragObjTypeName;
    const TargetColumns: TargetColumnValue[] = [];
    TargetColumns.push(
      {
        Name: 'Bezeichnung',
        Value: new String(auftragsName),
      },
      {
        Name: 'Status',
        Value: new Number(state),
      },
      {
        Name: 'Typ',
        Value: new Number(2), //Wartungsauftrag
      },
      {
        Name: 'Date',
        Value: new Date(auftragDate),
      },
      {
        Name: 'Nummer',
        Value: new String(auftragsNumber),
      },
      {
        Name: 'Bemerkung',
        Value: new String(auftragsRemark),
      },
    );

    query.TargetColumns = TargetColumns;
    ret.ObjectSpaceName = 'BaSYS.Complete';
    ret.Object = query;

    return ret;
  }

  public queryMtObjectFromEntity(entity: DataEntity): Observable<MtBase | undefined> {
    return this.getObjectTypeValue(entity.objectType).pipe(
      concatMap((otView) => {
        const query = this.createMtObjectFromDataEntityQuery(entity, otView);
        return this.abcCore.data.executeReadObjectsQuery(query);
      }),
      map((results) => {
        return this.getResultObject<MtBase>(results);
      }),
    );
  }

  private async createInsertMtObjectQuery(entity: DataEntity): Promise<PersistObjectModel | undefined> {
    return await WorkService.createInsertMtObjectQueryFromEntity(
      this.objectSpaceService,
      this.abcCore.data,
      entity,
      PersistMode.Insert,
    );
  }

  public static async createInsertMtObjectQueryFromEntity(
    objectSpaceService: ObjectSpaceService,
    dataService: CoreDataService,
    entity: DataEntity,
    persistMode: PersistMode = PersistMode.Insert,
  ): Promise<PersistObjectModel | undefined> {
    const otInstance = await WorkService.getOTNameOfEntity(objectSpaceService, entity);
    const isStreetOT = otInstance.isStreetOT;
    const otName = otInstance.OTName;
    const kind = otInstance.kind;
    const type = otInstance.type;

    if (otName) {
      if (isStreetOT) {
        return WorkService.createInsertMtObjectQueryStreet(dataService, entity, otName, persistMode, kind, type);
      } else {
        return WorkService.createInsertMtObjectQueryCore(dataService, entity, otName, persistMode, kind, type);
      }
    } else {
      return undefined;
    }
  }

  private static createInsertMtObjectQueryStreet(
    dataService: CoreDataService,
    entity: DataEntity,
    otName: string,
    persistMode: PersistMode,
    kind: number,
    type: number,
  ): Promise<PersistObjectModel | undefined> {
    let objectQuery: QueryObjectsModel | undefined = undefined;

    switch (otName) {
      case 'StrAbschnitt': {
        objectQuery = dataService.createSimpleQuery('', otName, `Id='` + entity.id + `'`, [
          dataService.createQueryColumn('Strasse.ParentId', 'ParentId'),
          dataService.createQueryColumn('Id', 'GeObjectId'),
          dataService.createQueryColumn('ObjectType', 'GeObjectType'),
          dataService.createQueryColumn(
            "Concat(Strasse.Concat(Name, Prefix(ToStr(LfdNr), '-')), ' (', Nummer, ')')",
            'Designation',
          ),
        ]);
        break;
      }

      case 'StrFlaeche': {
        objectQuery = dataService.createSimpleQuery('', otName, `Id='` + entity.id + `'`, [
          dataService.createQueryColumn('Abschnitt.Strasse.ParentId', 'ParentId'),
          dataService.createQueryColumn('Id', 'GeObjectId'),
          dataService.createQueryColumn('ObjectType', 'GeObjectType'),
          dataService.createQueryColumn(
            "Concat(Abschnitt.Concat(Strasse.Concat(Name, Prefix(ToStr(LfdNr), '-')), ' (', Nummer, ')'), ' #', LfdNr, ' (', ref:Art, ')')",
            'Designation',
          ),
        ]);
        break;
      }
      case 'StrInventarmodul': {
        objectQuery = dataService.createSimpleQuery('', otName, `Id='` + entity.id + `'`, [
          dataService.createQueryColumn('ParentId', 'ParentId'),
          dataService.createQueryColumn('Id', 'GeObjectId'),
          dataService.createQueryColumn('ObjectType', 'GeObjectType'),
          dataService.createQueryColumn('Inventarnummer', 'Designation'),
        ]);
        break;
      }
      case 'StrAufbruchVorgang': {
        objectQuery = dataService.createSimpleQuery('', otName, `Id='` + entity.id + `'`, [
          dataService.createQueryColumn('ParentId', 'ParentId'),
          dataService.createQueryColumn('Id', 'GeObjectId'),
          dataService.createQueryColumn('ObjectType', 'GeObjectType'),
          dataService.createQueryColumn('Bezeichnung', 'Designation'),
        ]);
        break;
      }
    }

    if (objectQuery) {
      return dataService
        .executeReadObjectsQuery(objectQuery)
        .pipe(
          map((data: any) => {
            if (Array.isArray(data) && data.length) {
              const obj = data[0];
              const ret: PersistObjectModel = new PersistObjectModel();
              let query: TargetObjectData = new TargetObjectData();

              query.Mode = persistMode;
              query.ObjectKey = new ObjectKey();

              query.ObjectKey.ObjectType = WorkService.MtObjectObjTypeName;
              const targetColumns: TargetColumnValue[] = [];
              for (const key of Object.keys(obj)) {
                targetColumns.push({
                  Name: key,
                  Value: obj[key],
                });
              }

              targetColumns.push({ Name: 'Id', Value: GUID.newGUID() });
              targetColumns.push({ Name: 'Typ', Value: type });
              targetColumns.push({ Name: 'Kind', Value: kind });

              query.TargetColumns = targetColumns;
              ret.ObjectSpaceName = 'BaSYS.Complete';
              ret.Object = query;

              return ret;
            } else {
              return undefined;
            }
          }),
        )
        .toPromise();
    } else {
      return Promise.resolve(undefined);
    }
  }

  private static createInsertMtObjectQueryCore(
    dataService: CoreDataService,
    entity: DataEntity,
    otName: string,
    persistMode: PersistMode,
    kind: number,
    type: number,
  ): Promise<PersistObjectModel | undefined> {
    const isGeoObjectType: boolean = ['CtGeoPoint', 'CtGeoLine', 'CtGeoArea'].some((n) => n === otName);
    const objectQuery = dataService.createSimpleQuery('', otName, `Id='` + entity.id + `'`, [
      dataService.createQueryColumn('ParentId', 'ParentId'),
      dataService.createQueryColumn('Id', 'GeObjectId'),
      dataService.createQueryColumn('ObjectType', 'GeObjectType'),
      isGeoObjectType
        ? dataService.createQueryColumn('Designation', 'Designation')
        : dataService.createQueryColumn('Bezeichnung', 'Designation'),
      dataService.createQueryColumn('IdRefStrasse', 'IdRefStrasse'),
    ]);
    return dataService
      .executeReadObjectsQuery(objectQuery)
      .pipe(
        map((data: any) => {
          if (Array.isArray(data) && data.length) {
            const obj = data[0];
            const ret: PersistObjectModel = new PersistObjectModel();
            let query: TargetObjectData = new TargetObjectData();

            query.Mode = persistMode;
            query.ObjectKey = new ObjectKey();

            query.ObjectKey.ObjectType = WorkService.MtObjectObjTypeName;
            const targetColumns: TargetColumnValue[] = [];
            for (const key of Object.keys(obj)) {
              targetColumns.push({
                Name: key,
                Value: obj[key],
              });
            }

            targetColumns.push({ Name: 'Id', Value: GUID.newGUID() });
            targetColumns.push({ Name: 'Typ', Value: type });
            targetColumns.push({ Name: 'Kind', Value: kind });

            query.TargetColumns = targetColumns;
            ret.ObjectSpaceName = 'BaSYS.Complete';
            ret.Object = query;

            return ret;
          } else {
            return undefined;
          }
        }),
      )
      .toPromise();
  }

  private static async getOTNameOfEntity(
    objectSpaceService: ObjectSpaceService,
    entity: DataEntity,
  ): Promise<{ OTName: string | undefined; isStreetOT: boolean; kind: number; type: number }> {
    let isStreet: boolean = false;
    let otName: string | undefined = undefined;
    let kind = 0;
    let type: number = 0;

    const otNames = [
      'AbwKnoten',
      'AbwLeitung',
      'WaKnoten',
      'WaLeitung',
      'GaKnoten',
      'GaLeitung',
      'EnKnoten',
      'EnTrassenabschnitt',
    ];

    const otNamesStreet = ['StrAbschnitt', 'StrFlaeche', 'StrInventarmodul', 'StrAufbruchVorgang'];
    const otNamesGeoObject = ['CtGeoPoint', 'CtGeoLine', 'CtGeoArea'];
    const otNamesKkA = ['KkAnlage'];

    for (let i = 0; i < otNames.length; i++) {
      let ot = otNames[i];
      if (await objectSpaceService.is(entity.objectType, ot)) {
        otName = ot;
        type = i % 2;
        kind = Math.trunc(i / 2);
        break;
      }
    }

    if (!otName) {
      for (let i = 0; i < otNamesStreet.length; i++) {
        let ot = otNamesStreet[i];
        if (await objectSpaceService.is(entity.objectType, ot)) {
          otName = ot;
          isStreet = true;
          break;
        }
      }
    }

    if (!otName) {
      for (let i = 0; i < otNamesGeoObject.length; i++) {
        let ot = otNamesGeoObject[i];
        if (await objectSpaceService.is(entity.objectType, ot)) {
          otName = ot;
          break;
        }
      }
    }
    if (!otName) {
      for (let i = 0; i < otNamesKkA.length; i++) {
        let ot = otNamesKkA[i];
        if (await objectSpaceService.is(entity.objectType, ot)) {
          otName = ot;
          break;
        }
      }
    }

    if (otName) {
      switch (otName) {
        case 'StrInventarmodul':
        case 'StrAufbruchVorgang': {
          kind = 5;
          type = 9;
          break;
        }
        case 'StrAbschnitt': {
          kind = 5;
          type = 1;
          break;
        }
        case 'StrFlaeche': {
          kind = 5;
          type = 2;
          break;
        }
        case 'CtGeoPoint': {
          kind = 4;
          type = 0;
          break;
        }
        case 'CtGeoLine': {
          kind = 4;
          type = 1;
          break;
        }
        case 'CtGeoArea': {
          kind = 4;
          type = 2;
          break;
        }
        case 'KkAnlage': {
          kind = 7;
          type = 9; //2-Flaeche, 9-Sonstiges, to be cleared up
          break;
        }
      }
    }

    return { OTName: otName, isStreetOT: isStreet, kind: kind, type: type };
  }

  public ensureMtObjectFromEntity(entity: DataEntity): Observable<MtBase | undefined> {
    return this.queryMtObjectFromEntity(entity).pipe(
      concatMap(async (mtObj) => {
        if (mtObj) {
          return mtObj;
        } else {
          const insertObjectQuery = await this.createInsertMtObjectQuery(entity);
          if (insertObjectQuery) {
            return await this.abcCore.data
              .executePersistObjectQuery(insertObjectQuery)
              .pipe(
                map((result) => {
                  if (result) {
                    mtObj = <MtBase>result;
                  }
                  return mtObj;
                }),
              )
              .toPromise();
          } else {
            return undefined;
          }
        }
      }),
    );
  }

  private createMtObjectFromDataEntityQuery(entity: DataEntity, otView: ObjectTypeInfoViewModel): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = WorkService.MtObjectObjTypeName;
    query.OPath = "IdMasterObject = '" + entity.id + "' And ObjectTypeMasterObject = " + otView.value.toString();

    const columns: QueryColumn[] = [];

    columns.push({ Name: 'Id', OPath: 'Id' });
    columns.push({ Name: 'ObjectType', OPath: 'TypeName()' });

    query.Columns = columns;
    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];

    return queryDocument;
  }

  public queryExistingAuftrag(auftragName: string, auftragNumber: string) {
    const query = this.createExistingAuftragQuery(auftragName, auftragNumber);
    return this.abcCore.data.executeReadObjectsQuery(query);
  }

  private createExistingAuftragQuery(auftragName: string, auftragNumber: string): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = WorkService.AuftragObjTypeName;
    query.OPath = "Bezeichnung = '" + auftragName + "'";

    const columns: QueryColumn[] = [];

    columns.push({ Name: 'Id', OPath: 'ParentId' });
    columns.push({ Name: 'ObjectType', OPath: 'TypeName()' });

    query.Columns = columns;
    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];

    return queryDocument;
  }

  private createWorkPersistQuery(
    objectInfos: MtWorkGenerationInfo[],
    workState: WorkState,
    workTypeId: string,
    orderId: string,
    source: string,
    excludeObjects: MtBase[],
  ): Observable<PersistObjectsModel> {
    return this.queryWorkType(workTypeId).pipe(
      map((mtWorkType) =>
        WorkService.createWorkPersistQueryForWorkType(
          objectInfos,
          workState,
          mtWorkType,
          orderId,
          source,
          excludeObjects,
        ),
      ),
    );
  }

  public static createWorkPersistQueryForWorkType(
    objectInfos: MtWorkGenerationInfo[],
    workState: WorkState,
    mtWorkType: MtWorkType,
    orderId: string,
    source: string,
    excludeObjects: MtBase[],
    notifyWorkCreated?: (targetColumns: TargetColumnValue[], workType: MtWorkType) => void,
  ): PersistObjectsModel {
    let result: PersistObjectsModel = new PersistObjectsModel();

    const WORKMODE_TICKETONLY_ID = 'Workmode_TicketOnly_ID';

    let objQueries: TargetObjectData[] = [];
    for (let obj of objectInfos) {
      if (mtWorkType && !excludeObjects?.find((o) => o.Id === obj.MtObjectId)) {
        let workPersistQuery: TargetObjectData = new TargetObjectData();

        workPersistQuery.Mode = notifyWorkCreated ? PersistMode.Merge : PersistMode.Insert;
        workPersistQuery.ObjectKey = new ObjectKey();

        workPersistQuery.ObjectKey.ObjectType = WorkService.MtWorkObjTypeName;
        const targetColumns: TargetColumnValue[] = [];
        targetColumns.push(
          {
            Name: 'Designation',
            Value: new String(mtWorkType.designation),
          },
          {
            Name: 'ParentId',
            Value: new String(obj.MtObjectId),
          },
          {
            Name: 'State',
            Value: new Number(workState),
          },
          {
            Name: 'WorkTypeId',
            Value: new String(mtWorkType.Id),
          },
          {
            Name: 'IdRefObjWorkType',
            Value: new String(obj.MtObjWorkTypeId ?? ''),
          },
          {
            Name: 'ScheduledExecutionDate',
            Value: new String(this.formatDate(obj.ScheduledWorkExecDate)),
          },
          {
            Name: 'EarliestExecutionDate',
            Value: new String(this.formatDate(obj.EarliestWorkExecDate, true)),
          },
          {
            Name: 'LatestExecutionDate',
            Value: new String(this.formatDate(obj.LatestWorkExecDate, true)),
          },
          {
            Name: 'ExecTime',
            Value: new Number(obj.WorkExecTime ?? 0),
          },
          {
            Name: 'AuftragsId',
            Value: new String(orderId === WORKMODE_TICKETONLY_ID ? '' : (orderId ?? '')),
          },
          {
            Name: 'Source',
            Value: new String(source ?? ''),
          },
        );
        if (notifyWorkCreated) {
          const workId: string = GUID.newGUID();
          targetColumns.push({
            Name: 'Id',
            Value: workId,
          });

          notifyWorkCreated(targetColumns, mtWorkType);
        }
        workPersistQuery.TargetColumns = targetColumns;
        objQueries.push(workPersistQuery);
      }
    }

    result.Objects = [objQueries];
    return result;
  }

  public static formatDate(xDate: Date | undefined, nullable: boolean = false): string {
    if (!nullable) {
      if (!xDate) xDate = new Date();
    }

    if (xDate) return this.toISODate(xDate);
    //.split('T')[0];
    else return '';
  }

  private static toISODate(date: Date): string {
    let d = new Date(date);
    let month: string = '' + (d.getMonth() + 1);
    let day: string = '' + d.getDate();
    let year: string = '' + d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
  }

  public queryWorkType(workTypeId: string): Observable<MtWorkType> {
    const queryDocument = WorkService.createWorkTypeQuery("Id = '" + workTypeId + "'");
    return this.abcCore.data.executeReadObjectsQuery(queryDocument).pipe(
      map((results) => {
        return this.getResultObject<MtWorkType>(results);
      }),
    );
  }

  public static createWorkTypeQuery(filter: string): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = 'MtWorkType';
    query.OPath = filter;

    const columns: QueryColumn[] = [];
    columns.push({ Name: 'Id', OPath: 'Id' });
    columns.push({ Name: 'designation', OPath: 'Designation' });
    columns.push({ Name: 'execTime', OPath: 'ExecTime' });
    columns.push({ Name: 'leadTime', OPath: 'LeadTime' });
    columns.push({ Name: 'objectTypeDefinition', OPath: 'ObjectDefinition' });
    query.Columns = columns;

    const queryTaskType: Query = new Query();
    queryTaskType.OPath = 'TaskTypes';
    queryTaskType.Name = 'taskTypes';
    const qtColumns: QueryColumn[] = [];
    qtColumns.push({ Name: 'Id', OPath: 'Id' });
    qtColumns.push({ Name: 'designation', OPath: 'Designation' });
    qtColumns.push({ Name: 'sortOrder', OPath: 'SortOrder' });
    qtColumns.push({ Name: 'allowMultiSelectResults', OPath: 'AllowMultiSelelectResults' }); //the opath here is a typo in data structure
    qtColumns.push({ Name: 'allowSelectResultsFromSingleCategory', OPath: 'AllowSelectResultsFromSingleCategory' });
    qtColumns.push({ Name: 'resultsRequired', OPath: 'ResultsRequired' });
    qtColumns.push({ Name: 'unitPrice', OPath: 'UnitPrice' });
    qtColumns.push({ Name: 'description', OPath: 'Description' });
    qtColumns.push({ Name: 'relevantForEdges', OPath: 'RelevantForEdges' });
    qtColumns.push({ Name: 'relevantForEndNode', OPath: 'RelevantForEndNode' });
    qtColumns.push({ Name: 'relevantForMidNodes', OPath: 'RelevantForMidNodes' });
    qtColumns.push({ Name: 'relevantForStartNode', OPath: 'RelevantForStartNode' });

    queryTaskType.Columns = qtColumns;

    const queryPosType: Query = new Query();
    queryPosType.OPath = 'PositionType';
    queryPosType.Name = 'positionTypes';
    const ptColumns: QueryColumn[] = [];
    ptColumns.push({ Name: 'Id', OPath: 'Id' });
    ptColumns.push({ Name: 'designation', OPath: 'Designation' });
    queryPosType.Columns = ptColumns;
    queryTaskType.ObjectQueries = [queryPosType];
    query.ObjectQueries = [queryTaskType];

    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];

    return queryDocument;
  }

  private createMaintenanceOrderAsDropDownListQuery() {
    return this.createDropDownListQueryCore(
      'Auftrag',
      'Typ = 2 And ParentId = $CurrentMunicipalityVariantId',
      'Bezeichnung',
    );
  }

  public createOrderAsDropDownListQuery(): Observable<ArrayBuffer> {
    return this.createDropDownListQueryCore(
      'Auftrag',
      'ParentId = $CurrentMunicipalityVariantId And Typ = 2',
      'Bezeichnung',
    );
  }

  public createWorkTypeAsDropDownListQuery(withObjectTypeDefinitionColumn: boolean = false): Observable<ArrayBuffer> {
    return from(this.createWorkTypeAsDropDownListQueryCore(withObjectTypeDefinitionColumn));
  }

  private async createWorkTypeAsDropDownListQueryCore(
    withObjectTypeDefinitionColumn: boolean = false,
  ): Promise<ArrayBuffer> {
    if (withObjectTypeDefinitionColumn) {
      return lastValueFrom(
        this.createDropDownListQueryWithObjectTypeDefinition(
          'MtWorkType',
          await AppService.getWorkTypeFilterCore(this.userService),
        ),
      );
    } else {
      return lastValueFrom(
        this.createDropDownListQueryWithLeadTime(
          'MtWorkType',
          await AppService.getWorkTypeFilterCore(this.userService),
        ),
      );
    }
  }

  public createDropDownListQueryCore(
    objectType: string,
    oPath: string,
    displayTextOPath: string = 'Designation',
    displayTextFieldName: string = 'Description',
    parameters?: ParameterValue[],
  ): Observable<ArrayBuffer> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = objectType;
    query.OPath = oPath;

    const columns: QueryColumn[] = [];

    columns.push({ Name: 'Id', OPath: 'Id' });
    columns.push({ Name: 'ObjectType', OPath: 'ObjectType' });
    columns.push({ Name: displayTextFieldName, OPath: displayTextOPath, SortOrder: QueryColumnSortOrder.Ascending });

    query.Columns = columns;
    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];
    if (parameters) {
      queryDocument.Parameters = parameters;
    }

    return this.abcCore.data.executeReadObjectsQuery(queryDocument);
  }

  public createDropDownListQueryWithObjectTypeDefinition(
    objectType: string,
    oPath: string,
    displayTextOPath: string = 'Designation',
    displayTextFieldName: string = 'Description',
    parameters?: ParameterValue[],
  ): Observable<ArrayBuffer> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = objectType;
    query.OPath = oPath;

    const columns: QueryColumn[] = [];

    columns.push({ Name: 'Id', OPath: 'Id' });
    columns.push({ Name: 'ObjectType', OPath: 'ObjectType' });
    columns.push({ Name: displayTextFieldName, OPath: displayTextOPath, SortOrder: QueryColumnSortOrder.Ascending });
    columns.push({ Name: 'objectTypeDefinition', OPath: 'ObjectDefinition' });
    columns.push({ Name: 'leadTime', OPath: 'LeadTime' });
    query.Columns = columns;
    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];
    if (parameters) {
      queryDocument.Parameters = parameters;
    }

    return this.abcCore.data.executeReadObjectsQuery(queryDocument);
  }

  private createDropDownListQueryWithLeadTime(
    objectType: string,
    oPath: string,
    displayTextOPath: string = 'Designation',
    displayTextFieldName: string = 'Description',
    parameters?: ParameterValue[],
  ): Observable<ArrayBuffer> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = objectType;
    query.OPath = oPath;

    const columns: QueryColumn[] = [];

    columns.push({ Name: 'Id', OPath: 'Id' });
    columns.push({ Name: 'ObjectType', OPath: 'ObjectType' });
    columns.push({ Name: displayTextFieldName, OPath: displayTextOPath, SortOrder: QueryColumnSortOrder.Ascending });
    columns.push({ Name: 'leadTime', OPath: 'LeadTime' });
    query.Columns = columns;
    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];
    if (parameters) {
      queryDocument.Parameters = parameters;
    }

    return this.abcCore.data.executeReadObjectsQuery(queryDocument);
  }

  public isEmptyObject(object: any) {
    return CompareHelper.isEmptyObject(object);
  }

  public queryPicklistItems(parameter: any): Observable<any> {
    const observable$ = new Observable((observer) => {
      const queryPicklistItems: ReadPicklistItemsModel = new ReadPicklistItemsModel();
      queryPicklistItems.Indent = true;
      queryPicklistItems.OutputMode = 0;
      queryPicklistItems.Name = parameter;
      this.abcCore.data.executeReadPicklistsItemsQuery(queryPicklistItems).subscribe((result) => {
        observer.next(result);
      });
    });
    return observable$;
  }

  public getObjectTypeValue(objectType: string | number): Observable<ObjectTypeInfoViewModel> {
    return this.objectSpaceService.getObjectType(objectType);
  }

  public getRelatedResources(
    selectedKeys: any[],
    dataRows: any[],
    keyExpr: string,
    parentIdColumnName: string,
    refResourceIdsColumnName: string,
    stateColumnName: string,
    selectChildrenResources: boolean = true,
    selectReferencedResources: boolean = false,
  ): any[] {
    if (!dataRows?.length || !selectedKeys?.length || !keyExpr) {
      return [];
    }

    const visited = new Set<any>();
    const { childrenMap, referenceMap, rowMap } = this.buildResourceGraph(
      dataRows,
      keyExpr,
      parentIdColumnName,
      refResourceIdsColumnName,
      stateColumnName,
    );

    const resultChildren = new Set<any>();
    let resultReferenced = new Set<any>();

    if (selectReferencedResources) {
      for (const id of selectedKeys) {
        const refKeys = referenceMap.get(id);
        if (refKeys) {
          refKeys.forEach((e) => resultReferenced.add(e));
        }
      }
    }

    const isValid = (id: any) => rowMap.get(id)?.[stateColumnName] !== 0;
    let validSelectedKeysForChildren = selectedKeys.filter(isValid);

    if (selectReferencedResources) {
      validSelectedKeysForChildren = [...new Set([...validSelectedKeysForChildren, ...resultReferenced])];
    }

    const stack = [...validSelectedKeysForChildren];
    while (stack.length) {
      const current = stack.pop();
      if (visited.has(current)) continue;
      visited.add(current);

      if (selectChildrenResources) {
        const children = childrenMap.get(current) ?? [];
        for (const child of children) {
          if (!visited.has(child)) {
            resultChildren.add(child);
            stack.push(child);
          }
        }
      }
    }

    return Array.from(new Set([...resultChildren, ...resultReferenced]));
  }

  public buildResourceGraph(
    dataRows: any[],
    keyExpr: string,
    parentIdColumnName: string,
    refResourceIdsColumnName: string,
    stateColumnName: string,
  ): {
    childrenMap: Map<any, any[]>;
    referenceMap: Map<any, any[]>;
    rowMap: Map<any, any>;
  } {
    const childrenMap = new Map<any, any[]>();
    const referenceMap = new Map<any, any[]>();
    const rowMap = new Map<any, any>();

    for (const row of dataRows) {
      const id = row[keyExpr];
      const parentId = row[parentIdColumnName];
      const refIds = row[refResourceIdsColumnName];
      const state = row[stateColumnName];

      if (parentId && state !== 0) {
        if (!childrenMap.has(parentId)) {
          childrenMap.set(parentId, []);
        }
        childrenMap.get(parentId).push(id);
      }

      if (refIds) {
        referenceMap.set(
          id,
          refIds.split(',').map((x) => x.trim()),
        );
      }

      rowMap.set(id, row);
    }

    return { childrenMap, referenceMap, rowMap };
  }
}
